import { get } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import {
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
} from "@tanstack/react-query";
import axios, { type AxiosError } from "axios";
import { z } from "zod";

const voiceNotePresignedUrlResponseSchema = z.object({
  data: z.object({
    type: z.string(),
    attributes: z.object({
      presignedUrl: z.string(),
      fileKey: z.string(),
      rootFolder: z.string(),
    }),
  }),
});
export type VoiceNotePresignedUrlResponse = z.infer<typeof voiceNotePresignedUrlResponseSchema>;

export const uploadVoiceNoteToS3RequestSchema = z.object({
  placementCandidateId: z.string(),
  type: z.string(),
  fileBlob: z.instanceof(Blob),
});
export type UploadVoiceNoteToS3Params = z.infer<typeof uploadVoiceNoteToS3RequestSchema>;

export const uploadVoiceNoteToS3ResponseSchema = z.object({
  rootFolder: z.string(),
  fileKey: z.string(),
});
export type UploadVoiceNoteToS3Response = z.infer<typeof uploadVoiceNoteToS3ResponseSchema>;

async function getPresignedVoiceNoteUploadUrl(params: { placementCandidateId: string }) {
  const { placementCandidateId } = params;

  const response = await get<undefined, VoiceNotePresignedUrlResponse>({
    url: `${environmentConfig.REACT_APP_BASE_API_URL}/placement-candidates/${placementCandidateId}/voice-note-presigned-urls`,
    responseSchema: voiceNotePresignedUrlResponseSchema,
  });

  return response.data.data.attributes; // { presignedUrl, fileKey, rootFolder }
}

export function useUploadVoiceNoteToS3(
  options: UseMutationOptions<
    UploadVoiceNoteToS3Response,
    AxiosError,
    UploadVoiceNoteToS3Params
  > = {}
): UseMutationResult<UploadVoiceNoteToS3Response, AxiosError, UploadVoiceNoteToS3Params> {
  return useMutation<UploadVoiceNoteToS3Response, AxiosError, UploadVoiceNoteToS3Params>({
    mutationFn: async (params) => {
      uploadVoiceNoteToS3RequestSchema.parse(params);

      const { placementCandidateId, fileBlob, type } = params;

      const { presignedUrl, fileKey, rootFolder } = await getPresignedVoiceNoteUploadUrl({
        placementCandidateId,
      });

      await axios.put(presignedUrl, fileBlob, {
        headers: {
          "Content-Type": type,
        },
      });

      return {
        rootFolder,
        fileKey,
      };
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.PRESIGNED_URL_UPLOAD_FAILURE,
    },
    ...options,
  });
}
